import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Box, Typography, Button, Paper, Alert, Snackbar,
    TextField, Stack, CircularProgress, Chip, Grid,
    FormControl, InputLabel, Select, MenuItem, Autocomplete,
    IconButton, Collapse, LinearProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, Card, CardContent, Tooltip,
    RadioGroup, FormControlLabel, Radio, FormLabel
} from '@mui/material';
import {
    Flight as FlightIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as CheckCircleIcon,
    Edit as DraftIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import { fetchDraftFormFor, fetchFormById, fromServerForm, upsertDraftForm, fetchAccommodations, fetchCountries, fetchSegments, TravelFormClient, saveCountries, saveSegments, saveAccommodations } from '../services/travel';
import { sendSubmissionNotification } from '../services/email';
import { useLocation } from 'react-router-dom';

const mockProjects: { number: string; name: string; client: string }[] = [
    { number: 'P2024-001', name: 'Digital Transformation Initiative', client: 'TechCorp Ltd' },
    { number: 'P2024-002', name: 'Infrastructure Modernization', client: 'GlobalBank PLC' },
    { number: 'P2023-045', name: 'Supply Chain Optimization', client: 'Manufacturing Co' },
    { number: 'P2024-003', name: 'Customer Experience Platform', client: 'RetailChain Inc' },
];

const mockCountries = [
    // Common/Frequently used countries (shown at top)
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', common: true },
    { code: 'US', name: 'United States', flag: '🇺🇸', common: true },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', common: true },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', common: true },
    { code: 'FR', name: 'France', flag: '🇫🇷', common: true },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', common: true },
    
    // All other countries (ISO 3166-1 alpha-2)
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', common: false },
    { code: 'AL', name: 'Albania', flag: '🇦🇱', common: false },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿', common: false },
    { code: 'AD', name: 'Andorra', flag: '🇦🇩', common: false },
    { code: 'AO', name: 'Angola', flag: '🇦🇴', common: false },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷', common: false },
    { code: 'AM', name: 'Armenia', flag: '🇦🇲', common: false },
    { code: 'AT', name: 'Austria', flag: '🇦🇹', common: false },
    { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', common: false },
    { code: 'BS', name: 'Bahamas', flag: '🇧🇸', common: false },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭', common: false },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', common: false },
    { code: 'BB', name: 'Barbados', flag: '🇧🇧', common: false },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾', common: false },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪', common: false },
    { code: 'BZ', name: 'Belize', flag: '🇧🇿', common: false },
    { code: 'BJ', name: 'Benin', flag: '🇧🇯', common: false },
    { code: 'BT', name: 'Bhutan', flag: '🇧🇹', common: false },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴', common: false },
    { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦', common: false },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼', common: false },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', common: false },
    { code: 'BN', name: 'Brunei', flag: '🇧🇳', common: false },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', common: false },
    { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', common: false },
    { code: 'BI', name: 'Burundi', flag: '🇧🇮', common: false },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭', common: false },
    { code: 'CM', name: 'Cameroon', flag: '🇨🇲', common: false },
    { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', common: false },
    { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', common: false },
    { code: 'TD', name: 'Chad', flag: '🇹🇩', common: false },
    { code: 'CL', name: 'Chile', flag: '🇨🇱', common: false },
    { code: 'CN', name: 'China', flag: '🇨🇳', common: false },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴', common: false },
    { code: 'KM', name: 'Comoros', flag: '🇰🇲', common: false },
    { code: 'CG', name: 'Congo', flag: '🇨🇬', common: false },
    { code: 'CD', name: 'Congo (DRC)', flag: '🇨🇩', common: false },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', common: false },
    { code: 'CI', name: 'Côte d\'Ivoire', flag: '🇨🇮', common: false },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷', common: false },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺', common: false },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾', common: false },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', common: false },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', common: false },
    { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', common: false },
    { code: 'DM', name: 'Dominica', flag: '🇩🇲', common: false },
    { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', common: false },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨', common: false },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬', common: false },
    { code: 'SV', name: 'El Salvador', flag: '🇸🇻', common: false },
    { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', common: false },
    { code: 'ER', name: 'Eritrea', flag: '🇪🇷', common: false },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪', common: false },
    { code: 'SZ', name: 'Eswatini', flag: '🇸🇿', common: false },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', common: false },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯', common: false },
    { code: 'FI', name: 'Finland', flag: '🇫🇮', common: false },
    { code: 'GA', name: 'Gabon', flag: '🇬🇦', common: false },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲', common: false },
    { code: 'GE', name: 'Georgia', flag: '🇬🇪', common: false },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭', common: false },
    { code: 'GR', name: 'Greece', flag: '🇬🇷', common: false },
    { code: 'GD', name: 'Grenada', flag: '🇬🇩', common: false },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹', common: false },
    { code: 'GN', name: 'Guinea', flag: '🇬🇳', common: false },
    { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', common: false },
    { code: 'GY', name: 'Guyana', flag: '🇬🇾', common: false },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹', common: false },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳', common: false },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', common: false },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺', common: false },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸', common: false },
    { code: 'IN', name: 'India', flag: '🇮🇳', common: false },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', common: false },
    { code: 'IR', name: 'Iran', flag: '🇮🇷', common: false },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶', common: false },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪', common: false },
    { code: 'IL', name: 'Israel', flag: '🇮🇱', common: false },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', common: false },
    { code: 'JM', name: 'Jamaica', flag: '🇯🇲', common: false },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', common: false },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴', common: false },
    { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿', common: false },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', common: false },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮', common: false },
    { code: 'KP', name: 'North Korea', flag: '🇰🇵', common: false },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', common: false },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼', common: false },
    { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', common: false },
    { code: 'LA', name: 'Laos', flag: '🇱🇦', common: false },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻', common: false },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧', common: false },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸', common: false },
    { code: 'LR', name: 'Liberia', flag: '🇱🇷', common: false },
    { code: 'LY', name: 'Libya', flag: '🇱🇾', common: false },
    { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', common: false },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹', common: false },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', common: false },
    { code: 'MG', name: 'Madagascar', flag: '🇲🇬', common: false },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼', common: false },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', common: false },
    { code: 'MV', name: 'Maldives', flag: '🇲🇻', common: false },
    { code: 'ML', name: 'Mali', flag: '🇲🇱', common: false },
    { code: 'MT', name: 'Malta', flag: '🇲🇹', common: false },
    { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭', common: false },
    { code: 'MR', name: 'Mauritania', flag: '🇲🇷', common: false },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺', common: false },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', common: false },
    { code: 'FM', name: 'Micronesia', flag: '🇫🇲', common: false },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩', common: false },
    { code: 'MC', name: 'Monaco', flag: '🇲🇨', common: false },
    { code: 'MN', name: 'Mongolia', flag: '🇲🇳', common: false },
    { code: 'ME', name: 'Montenegro', flag: '🇲🇪', common: false },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦', common: false },
    { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', common: false },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲', common: false },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦', common: false },
    { code: 'NR', name: 'Nauru', flag: '🇳🇷', common: false },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵', common: false },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱', common: false },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', common: false },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', common: false },
    { code: 'NE', name: 'Niger', flag: '🇳🇪', common: false },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬', common: false },
    { code: 'MK', name: 'North Macedonia', flag: '🇲🇰', common: false },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', common: false },
    { code: 'OM', name: 'Oman', flag: '🇴🇲', common: false },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰', common: false },
    { code: 'PW', name: 'Palau', flag: '🇵🇼', common: false },
    { code: 'PS', name: 'Palestine', flag: '🇵🇸', common: false },
    { code: 'PA', name: 'Panama', flag: '🇵🇦', common: false },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', common: false },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾', common: false },
    { code: 'PE', name: 'Peru', flag: '🇵🇪', common: false },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭', common: false },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', common: false },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', common: false },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦', common: false },
    { code: 'RO', name: 'Romania', flag: '🇷🇴', common: false },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', common: false },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼', common: false },
    { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳', common: false },
    { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨', common: false },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', common: false },
    { code: 'WS', name: 'Samoa', flag: '🇼🇸', common: false },
    { code: 'SM', name: 'San Marino', flag: '🇸🇲', common: false },
    { code: 'ST', name: 'Sao Tome and Principe', flag: '🇸🇹', common: false },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', common: false },
    { code: 'SN', name: 'Senegal', flag: '🇸🇳', common: false },
    { code: 'RS', name: 'Serbia', flag: '🇷🇸', common: false },
    { code: 'SC', name: 'Seychelles', flag: '🇸🇨', common: false },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', common: false },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', common: false },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰', common: false },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮', common: false },
    { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧', common: false },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴', common: false },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', common: false },
    { code: 'SS', name: 'South Sudan', flag: '🇸🇸', common: false },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', common: false },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', common: false },
    { code: 'SD', name: 'Sudan', flag: '🇸🇩', common: false },
    { code: 'SR', name: 'Suriname', flag: '🇸🇷', common: false },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', common: false },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', common: false },
    { code: 'SY', name: 'Syria', flag: '🇸🇾', common: false },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼', common: false },
    { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', common: false },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', common: false },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', common: false },
    { code: 'TL', name: 'Timor-Leste', flag: '🇹🇱', common: false },
    { code: 'TG', name: 'Togo', flag: '🇹🇬', common: false },
    { code: 'TO', name: 'Tonga', flag: '🇹🇴', common: false },
    { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹', common: false },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳', common: false },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷', common: false },
    { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', common: false },
    { code: 'TV', name: 'Tuvalu', flag: '🇹🇻', common: false },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬', common: false },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦', common: false },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', common: false },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾', common: false },
    { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', common: false },
    { code: 'VU', name: 'Vanuatu', flag: '🇻🇺', common: false },
    { code: 'VA', name: 'Vatican City', flag: '🇻🇦', common: false },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪', common: false },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', common: false },
    { code: 'YE', name: 'Yemen', flag: '🇾🇪', common: false },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲', common: false },
    { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', common: false },
];

const mockHotels: string[] = [
    'Marriott Hotel', 'Hilton Hotel', 'Premier Inn', 'Holiday Inn',
    'Sheraton Hotel', 'Hyatt Hotel', 'InterContinental', 'Radisson Blu'
];

interface TravelFormData {
    // Main Itinerary
    projectNumber: string;
    projectName: string;
    client: string;
    teamLeader: string;
    dutyOfficer: string;
    travelDateFrom: string;
    travelDateTo: string;
    countries: string[];
    clientContactName: string;
    clientContactEmail: string;
    clientContactPhone: string;
    fcdoRestrictions: string;

    // Accommodation entries
    accommodations: Array<{
        id: string; // client-side id
        serverId?: number; // DB id
        checkInDate: string;
        checkOutDate: string;
        location: string;
        hotelName: string;
        address: string;
        phone: string;
        nights: number;
    }>;

    // Travel segments
    travelSegments: Array<{
        id: string;
        travelDate: string;
        travelType: string;
        flightNumber: string;
        routeFrom: string;
        routeTo: string;
        departureTime: string;
        arrivalTime: string;
        bookingRef: string;
    }>;

    // Pre-Deployment Checks
    bambooUpdated: string;
    preDeploymentBrief: string;
    happyToTravelLocation: string;
    happyToTravelCompanions: string;
    safeHeatTraining: string;
    securityClearance: string;
    medicalClearance: string;
    visaObtained: string;
    doctorAdvisedNotTravel: string;
    preExistingConditionUnderstood: string;
    
    // Contact & Comms
    whatsappPhone: string;
    focalInCountryName: string;
    focalInCountryContact: string;
    clientContactNameAlt: string;
    clientContactDetailsAlt: string;
    travellingWithIod: string;
    travellingWithName: string;
    
    // Travel & Logistics
    airportTransfersArranged: string;
    inCountryTransport: string;
    itineraryForwarded: string;
    
    // Travel Type
    travelType: 'project' | 'travel';
    travelReason: string;

    // Form metadata
    status: 'draft' | 'complete' | 'submitted' | 'approved';
    lastSaved: string;
}

const defaultFormData: TravelFormData = {
    projectNumber: '',
    projectName: '',
    client: '',
    teamLeader: '',
    dutyOfficer: '',
    travelDateFrom: '',
    travelDateTo: '',
    countries: [],
    clientContactName: '',
    clientContactEmail: '',
    clientContactPhone: '',
    fcdoRestrictions: '',
    accommodations: [],
    travelSegments: [],
    
    // Pre-Deployment Checks
    bambooUpdated: '',
    preDeploymentBrief: '',
    happyToTravelLocation: '',
    happyToTravelCompanions: '',
    safeHeatTraining: '',
    securityClearance: '',
    medicalClearance: '',
    visaObtained: '',
    doctorAdvisedNotTravel: '',
    preExistingConditionUnderstood: '',
    
    // Contact & Comms
    whatsappPhone: '',
    focalInCountryName: '',
    focalInCountryContact: '',
    clientContactNameAlt: '',
    clientContactDetailsAlt: '',
    travellingWithIod: '',
    travellingWithName: '',
    
    // Travel & Logistics
    airportTransfersArranged: '',
    inCountryTransport: '',
    itineraryForwarded: '',
    
    // Travel Type
    travelType: 'project',
    travelReason: '',
    
    status: 'draft',
    lastSaved: ''
};

const TravelFormPage: React.FC = () => {
    const [formData, setFormData] = useState<TravelFormData>(defaultFormData);
    const [formId, setFormId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [expandedSections, setExpandedSections] = useState({
        accommodation: true,
        travel: true,
        preDeployment: true,
        contactComms: true,
        travelLogistics: true
    });
    const [emailPreview, setEmailPreview] = useState<string>('');
    const [showEmailDialog, setShowEmailDialog] = useState(false);

    const location = useLocation();

    // Load principal and server draft or specific form on mount
    useEffect(() => {
        (async () => {
            const params = new URLSearchParams(location.search);
            
            // If explicitly creating a new form, skip loading any drafts
            const isNewForm = params.get('new') === 'true';
            if (isNewForm) {
                setFormData(defaultFormData);
                setFormId(null);
                localStorage.removeItem('travel-form-draft');
                return;
            }
            
            // If editing specific form id via query param
            const editId = params.get('edit');
            if (editId && !Number.isNaN(Number(editId))) {
                try {
                    const form = await fetchFormById(Number(editId));
                    if (form) {
                        const mapped = fromServerForm(form);
                        setFormId(form.id!);
                        setFormData(prev => ({ ...prev, ...mapped } as TravelFormData));
                        
                        // Load accommodations
                        const acc = await fetchAccommodations(form.id!);
                        
                        // Load countries
                        const countries = await fetchCountries(form.id!);
                        
                        // Load segments
                        const segments = await fetchSegments(form.id!);
                        
                        // Format date helper
                        const formatDate = (dateStr: string) => {
                            if (!dateStr) return '';
                            return dateStr.split('T')[0];
                        };
                        
                        setFormData(prev => ({
                            ...prev,
                            countries: countries,
                            accommodations: acc.map((a, idx) => ({
                                id: `${Date.now()}-${idx}`,
                                serverId: a.id,
                                checkInDate: formatDate(a.check_in_date || ''),
                                checkOutDate: formatDate(a.check_out_date || ''),
                                location: a.location || '',
                                hotelName: a.hotel_name || '',
                                address: a.address || '',
                                phone: a.phone || '',
                                nights: a.nights ?? 1,
                            })),
                            travelSegments: segments.map((s: any) => ({
                                id: s.id,
                                travelDate: formatDate(s.travel_date || ''),
                                travelType: s.travel_type,
                                flightNumber: s.flight_number || '',
                                routeFrom: s.route_from || '',
                                routeTo: s.route_to || '',
                                departureTime: s.departure_time || '',
                                arrivalTime: s.arrival_time || '',
                                bookingRef: s.booking_ref || ''
                            }))
                        }));
                        setSnackbarMessage(`Loaded form #${form.id}`);
                        setSnackbarOpen(true);
                        return;
                    }
                } catch (e) {
                    console.warn('Failed to load form by id, will try draft');
                }
            }

            // Try server draft first
            try {
                const principal = await getClientPrincipal();
                const serverDraft = principal ? await fetchDraftFormFor(principal) : null;
                if (serverDraft) {
                    const mapped = fromServerForm(serverDraft);
                    setFormId(serverDraft.id!);
                    setFormData(prev => ({
                        ...prev,
                        ...mapped,
                    } as TravelFormData));
                    // Load accommodations
                    const acc = await fetchAccommodations(serverDraft.id!);
                    setFormData(prev => ({
                        ...prev,
                        accommodations: acc.map((a, idx) => ({
                            id: `${Date.now()}-${idx}`,
                            serverId: a.id,
                            checkInDate: a.check_in_date || '',
                            checkOutDate: a.check_out_date || '',
                            location: a.location || '',
                            hotelName: a.hotel_name || '',
                            address: a.address || '',
                            phone: a.phone || '',
                            nights: a.nights ?? 1,
                        }))
                    }));
                    setSnackbarMessage('Draft loaded from server');
                    setSnackbarOpen(true);
                    return;
                }
            } catch (e) {
                console.warn('Server draft load failed, falling back to local draft');
            }

            // Fallback: local draft
            const savedData = localStorage.getItem('travel-form-draft');
            if (savedData) {
                try {
                    const parsedData = JSON.parse(savedData);
                    setFormData(parsedData);
                    setSnackbarMessage('Draft form loaded');
                    setSnackbarOpen(true);
                } catch (error) {
                    console.error('Error loading saved form:', error);
                }
            }
        })();
    }, [location.search]);

    // Auto-save every 30 seconds (only if form has been saved at least once)
    useEffect(() => {
        const autoSaveInterval = setInterval(() => {
            if (formId && formData.status === 'draft' && (
                formData.projectNumber || formData.travelDateFrom || formData.countries.length > 0
            )) {
                saveAsDraft();
            }
        }, 30000); // 30 seconds

        return () => clearInterval(autoSaveInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, formId]);

    const saveAsDraft = useCallback(async () => {
        setIsSaving(true);
        try {
            const principal = await getClientPrincipal();
            if (!principal || !principal.userId) throw new Error('Not authenticated');

            // Build client form wrapper for service
            const clientForm: TravelFormClient = {
                formId: formId ?? undefined,
                projectNumber: formData.projectNumber,
                projectName: formData.projectName,
                client: formData.client,
                teamLeader: formData.teamLeader,
                dutyOfficer: formData.dutyOfficer,
                travelDateFrom: formData.travelDateFrom,
                travelDateTo: formData.travelDateTo,
                countries: formData.countries,
                clientContactName: formData.clientContactName,
                clientContactEmail: formData.clientContactEmail,
                clientContactPhone: formData.clientContactPhone,
                fcdoRestrictions: formData.fcdoRestrictions,
                accommodations: formData.accommodations.map(a => ({
                    clientId: a.id,
                    serverId: a.serverId,
                    checkInDate: a.checkInDate,
                    checkOutDate: a.checkOutDate,
                    location: a.location,
                    hotelName: a.hotelName,
                    address: a.address,
                    phone: a.phone,
                    nights: a.nights,
                })),
                travelSegments: formData.travelSegments,
                status: 'draft',
                lastSaved: formData.lastSaved,
                
                // Pre-Deployment Checks
                bambooUpdated: formData.bambooUpdated,
                preDeploymentBrief: formData.preDeploymentBrief,
                happyToTravelLocation: formData.happyToTravelLocation,
                happyToTravelCompanions: formData.happyToTravelCompanions,
                safeHeatTraining: formData.safeHeatTraining,
                securityClearance: formData.securityClearance,
                medicalClearance: formData.medicalClearance,
                visaObtained: formData.visaObtained,
                doctorAdvisedNotTravel: formData.doctorAdvisedNotTravel,
                preExistingConditionUnderstood: formData.preExistingConditionUnderstood,
                
                // Contact & Comms
                whatsappPhone: formData.whatsappPhone,
                focalInCountryName: formData.focalInCountryName,
                focalInCountryContact: formData.focalInCountryContact,
                clientContactNameAlt: formData.clientContactNameAlt,
                clientContactDetailsAlt: formData.clientContactDetailsAlt,
                travellingWithIod: formData.travellingWithIod,
                travellingWithName: formData.travellingWithName,
                
                // Travel & Logistics
                airportTransfersArranged: formData.airportTransfersArranged,
                inCountryTransport: formData.inCountryTransport,
                itineraryForwarded: formData.itineraryForwarded,
                
                // Travel Type
                travelType: formData.travelType,
                travelReason: formData.travelReason,
            };

            const saved = await upsertDraftForm(clientForm, principal);
            setFormId(saved.id!);

            // Save all related data (countries, accommodations, segments)
            if (saved.id) {
                await saveCountries(saved.id, formData.countries);
                await saveAccommodations(saved.id, formData.accommodations);
                await saveSegments(saved.id, formData.travelSegments);
            }

            const updatedFormData = {
                ...formData,
                lastSaved: new Date().toLocaleTimeString(),
                status: 'draft' as const
            };
            localStorage.setItem('travel-form-draft', JSON.stringify(updatedFormData));
            setFormData(updatedFormData);
            
            // Navigate back to My Travel page
            setSnackbarMessage('Draft saved successfully');
            setSnackbarOpen(true);
            setTimeout(() => {
                window.location.href = '/#/my-travel';
            }, 1000);
        } catch (e) {
            console.error('Draft save failed', e);
            setError('Save failed. Check your connection and try again.');
        } finally {
            setIsSaving(false);
        }
    }, [formData, formId]);

    const addAccommodation = () => {
        const newAccommodation = {
            id: `${Date.now()}`,
            checkInDate: '',
            checkOutDate: '',
            location: '',
            hotelName: '',
            address: '',
            phone: '',
            nights: 1
        };

        setFormData(prev => ({
            ...prev,
            accommodations: [...prev.accommodations, newAccommodation]
        }));
    };

    const removeAccommodation = (index: number) => {
        // Just remove from local state - will be saved on next auto-save
        setFormData(prev => ({
            ...prev,
            accommodations: prev.accommodations.filter((_, i) => i !== index)
        }));
    };

    const addTravelSegment = () => {
        const newSegment = {
            id: Date.now().toString(),
            travelDate: '',
            travelType: 'Flight',
            flightNumber: '',
            routeFrom: '',
            routeTo: '',
            departureTime: '',
            arrivalTime: '',
            bookingRef: ''
        };

        setFormData(prev => ({
            ...prev,
            travelSegments: [...prev.travelSegments, newSegment]
        }));
    };

    const validateForm = () => {
        const errors = [];
        // Project Number only required for project travel
        if (formData.travelType === 'project' && !formData.projectNumber) {
            errors.push('Project Number is required');
        }
        // Travel Reason required for non-project travel
        if (formData.travelType === 'travel' && !formData.travelReason) {
            errors.push('Travel Reason is required');
        }
        if (!formData.travelDateFrom) errors.push('Travel From Date is required');
        if (!formData.travelDateTo) errors.push('Travel To Date is required');
        if (formData.countries.length === 0) errors.push('At least one country is required');

        return errors;
    };

    const calculateProgress = () => {
        let completedFields = 0;
        // For project travel: 6 fields, for general travel: 6 fields (with travel reason instead of project number)
        const totalRequiredFields = 6;

        if (formData.travelType === 'project' && formData.projectNumber) completedFields++;
        if (formData.travelType === 'travel' && formData.travelReason) completedFields++;
        if (formData.travelDateFrom) completedFields++;
        if (formData.travelDateTo) completedFields++;
        if (formData.countries.length > 0) completedFields++;
        if (formData.clientContactName) completedFields++;
        if (formData.accommodations.length > 0) completedFields++;

        return Math.round((completedFields / totalRequiredFields) * 100);
    };

    const generateEmailPreview = () => {
        const duration = formData.travelDateFrom && formData.travelDateTo
            ? Math.ceil((new Date(formData.travelDateTo).getTime() - new Date(formData.travelDateFrom).getTime()) / (1000 * 60 * 60 * 24))
            : 0;

        const countryNames = formData.countries.map(code =>
            mockCountries.find(c => c.code === code)?.name
        ).filter(Boolean);

        return `Subject: Travel Form Submitted - [User Name] - ${countryNames.join(', ')} (${formData.travelDateFrom} to ${formData.travelDateTo})

Travel Request Details:
Staff Member: [User Name]
Project: ${formData.projectName} (${formData.projectNumber})
Client: ${formData.client}
Destination: ${countryNames.join(', ')}
Travel Dates: ${formData.travelDateFrom} to ${formData.travelDateTo}
Duration: ${duration} days

${formData.accommodations.length > 0 ? 'Accommodations: ' + formData.accommodations.length + ' bookings planned' : ''}
${formData.travelSegments.length > 0 ? 'Travel Segments: ' + formData.travelSegments.length + ' flights/transport planned' : ''}

Client Contact: ${formData.clientContactName} (${formData.clientContactEmail})
${formData.fcdoRestrictions ? 'FCDO Notes: ' + formData.fcdoRestrictions : ''}

[View Full Details Button] [Approve Travel Button]`;
    };

    const handleSubmit = async () => {
        console.log('Submit clicked - starting validation');
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            console.log('Validation failed:', validationErrors);
            setError(validationErrors.join(', '));
            return;
        }

        console.log('Validation passed - submitting form');
        setIsLoading(true);
        setError(null);

        try {
            const principal = await getClientPrincipal();
            console.log('Principal:', principal);
            if (!principal || !principal.userId) throw new Error('Not authenticated');

            // Build client form for API
            const clientForm: TravelFormClient = {
                formId: formId ?? undefined,
                projectNumber: formData.projectNumber,
                projectName: formData.projectName,
                client: formData.client,
                teamLeader: formData.teamLeader,
                dutyOfficer: formData.dutyOfficer,
                travelDateFrom: formData.travelDateFrom,
                travelDateTo: formData.travelDateTo,
                countries: formData.countries,
                clientContactName: formData.clientContactName,
                clientContactEmail: formData.clientContactEmail,
                clientContactPhone: formData.clientContactPhone,
                fcdoRestrictions: formData.fcdoRestrictions,
                accommodations: formData.accommodations.map(acc => ({
                    clientId: acc.id,
                    serverId: acc.serverId,
                    checkInDate: acc.checkInDate,
                    checkOutDate: acc.checkOutDate,
                    location: acc.location,
                    hotelName: acc.hotelName,
                    address: acc.address,
                    phone: acc.phone,
                    nights: acc.nights
                })),
                travelSegments: formData.travelSegments,
                status: 'submitted',
                lastSaved: new Date().toLocaleTimeString(),
                
                // Pre-Deployment Checks
                bambooUpdated: formData.bambooUpdated,
                preDeploymentBrief: formData.preDeploymentBrief,
                happyToTravelLocation: formData.happyToTravelLocation,
                happyToTravelCompanions: formData.happyToTravelCompanions,
                safeHeatTraining: formData.safeHeatTraining,
                securityClearance: formData.securityClearance,
                medicalClearance: formData.medicalClearance,
                visaObtained: formData.visaObtained,
                doctorAdvisedNotTravel: formData.doctorAdvisedNotTravel,
                preExistingConditionUnderstood: formData.preExistingConditionUnderstood,
                
                // Contact & Comms
                whatsappPhone: formData.whatsappPhone,
                focalInCountryName: formData.focalInCountryName,
                focalInCountryContact: formData.focalInCountryContact,
                clientContactNameAlt: formData.clientContactNameAlt,
                clientContactDetailsAlt: formData.clientContactDetailsAlt,
                travellingWithIod: formData.travellingWithIod,
                travellingWithName: formData.travellingWithName,
                
                // Travel & Logistics
                airportTransfersArranged: formData.airportTransfersArranged,
                inCountryTransport: formData.inCountryTransport,
                itineraryForwarded: formData.itineraryForwarded,
                
                // Travel Type
                travelType: formData.travelType,
                travelReason: formData.travelReason,
            };

            console.log('Calling API with:', clientForm);
            // Save to database via API
            const savedForm = await upsertDraftForm(clientForm, principal);
            console.log('API response:', savedForm);
            
            // Save related data (countries, accommodations, segments)
            if (savedForm.id) {
                console.log('Saving countries...');
                await saveCountries(savedForm.id, formData.countries);
                
                console.log('Saving accommodations...');
                await saveAccommodations(savedForm.id, formData.accommodations);
                
                console.log('Saving segments...');
                await saveSegments(savedForm.id, formData.travelSegments);
                
                console.log('All data saved successfully!');
            }
            
            // Send submission notification to travel ops
            await sendSubmissionNotification({
                owner_email: principal.userDetails || '',
                owner_name: principal.name || 'Unknown',
                project_number: formData.projectNumber,
                project_name: formData.projectName,
                client: formData.client,
                travel_date_from: formData.travelDateFrom,
                travel_date_to: formData.travelDateTo,
                id: savedForm.id || 0,
                travel_type: formData.travelType
            });
            
            // Show email preview with current form data
            setEmailPreview(generateEmailPreview());
            setShowEmailDialog(true);

            // Clear form and reset to blank state
            setFormData(defaultFormData);
            setFormId(null);
            localStorage.removeItem('travel-form-draft');

            setSnackbarMessage('Travel form submitted successfully! Email sent to travel@iodparc.com');
            setSnackbarOpen(true);
            
            // Navigate back to My Travel page
            setTimeout(() => {
                window.location.href = '/#/my-travel';
            }, 2000);

        } catch (error) {
            console.error('Submit error:', error);
            setError('Failed to submit travel form. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const progress = calculateProgress();

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 3 }}>
                {/* Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <FlightIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Travel Form
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Complete your travel details with auto-save functionality
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, color: 'warning.main' }}>
                                This must be completed before any travel.
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Chip
                                label={formData.status.toUpperCase()}
                                color={formData.status === 'draft' ? 'warning' : 'success'}
                                icon={formData.status === 'draft' ? <DraftIcon /> : <CheckCircleIcon />}
                            />
                            {isSaving && (
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Saving...
                                </Typography>
                            )}
                            {formData.lastSaved && !isSaving && (
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Draft saved at {formData.lastSaved}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    {/* Progress Bar */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Form Progress: {progress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                </Paper>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Main Itinerary Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Main Itinerary Details
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Travel Type */}
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="travel-type-label">Travel Type</InputLabel>
                                <Select
                                    labelId="travel-type-label"
                                    value={formData.travelType}
                                    label="Travel Type"
                                    onChange={(e) => setFormData(prev => ({ ...prev, travelType: e.target.value as 'project' | 'travel' }))}
                                >
                                    <MenuItem value="project">Project Travel</MenuItem>
                                    <MenuItem value="travel">Travel</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Travel Reason - only show for non-project travel */}
                        {formData.travelType === 'travel' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Travel Reason"
                                    required
                                    value={formData.travelReason}
                                    onChange={(e) => setFormData(prev => ({ ...prev, travelReason: e.target.value }))}
                                    placeholder="e.g., Conference, Training, Team Meeting"
                                    helperText="Please specify the reason for your travel"
                                />
                            </Grid>
                        )}

                        {/* Spacer for layout - only show for travel type */}
                        {formData.travelType === 'travel' && <Grid item xs={12} md={6} />}

                        {/* Project Number - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Project Number"
                                    required
                                    value={formData.projectNumber}
                                    onChange={(e) => {
                                        let value = e.target.value.toUpperCase();
                                        // Ensure it starts with P-
                                        if (!value.startsWith('P-') && value.length > 0) {
                                            value = 'P-' + value.replace(/^P-?/, '');
                                        }
                                        setFormData(prev => ({ ...prev, projectNumber: value }));
                                    }}
                                    helperText="Project numbers start with P-"
                                    placeholder="P-3565"
                                />
                            </Grid>
                        )}

                        {/* Project Name - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Project Name"
                                    value={formData.projectName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, projectName: e.target.value }))}
                                    placeholder="Enter project name"
                                />
                            </Grid>
                        )}

                        {/* Client - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Client"
                                    value={formData.client}
                                    onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                                />
                            </Grid>
                        )}

                        {/* Team Leader - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Team Leader"
                                    value={formData.teamLeader}
                                    onChange={(e) => setFormData(prev => ({ ...prev, teamLeader: e.target.value }))}
                                    placeholder="Enter team leader name"
                                />
                            </Grid>
                        )}

                        {/* Duty Officer - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Duty Officer"
                                    value={formData.dutyOfficer}
                                    onChange={(e) => setFormData(prev => ({ ...prev, dutyOfficer: e.target.value }))}
                                    placeholder="Enter duty officer name"
                                    helperText="You must have an assigned duty officer within IOD PARC who is not travelling with you. For example: your team leader, workstream lead, cluster lead or security focal point. This duty officer will be your first point of contact during your travel and you must establish the best means of communication with them before you travel."
                                />
                            </Grid>
                        )}

                        {/* Travel Dates */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Travel From"
                                value={formData.travelDateFrom}
                                onChange={(e) => setFormData(prev => ({ ...prev, travelDateFrom: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Travel To"
                                value={formData.travelDateTo}
                                onChange={(e) => setFormData(prev => ({ ...prev, travelDateTo: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>

                        {/* Countries */}
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={mockCountries.sort((a, b) => (b.common ? 1 : 0) - (a.common ? 1 : 0))}
                                getOptionLabel={(option) => `${option.flag} ${option.name}`}
                                value={mockCountries.filter(c => formData.countries.includes(c.code))}
                                onChange={(_, newValue) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        countries: newValue.map(v => v.code)
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Countries"
                                        required
                                        helperText="Select destination countries"
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            variant="outlined"
                                            label={`${option.flag} ${option.name}`}
                                            {...getTagProps({ index })}
                                            key={option.code}
                                        />
                                    ))
                                }
                            />
                        </Grid>

                        {/* Client Contact Email - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Client Contact Email"
                                    type="email"
                                    value={formData.clientContactEmail}
                                    onChange={(e) => setFormData(prev => ({ ...prev, clientContactEmail: e.target.value }))}
                                />
                            </Grid>
                        )}

                        {/* Client Contact Phone - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Client Contact Phone (If known)"
                                    value={formData.clientContactPhone}
                                    onChange={(e) => setFormData(prev => ({ ...prev, clientContactPhone: e.target.value }))}
                                />
                            </Grid>
                        )}

                        {/* FCDO Restrictions - only show for project travel */}
                        {formData.travelType === 'project' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="FCDO Travel Restrictions & Notes"
                                    value={formData.fcdoRestrictions}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fcdoRestrictions: e.target.value }))}
                                    helperText={formData.countries.length > 0
                                        ? `Check FCDO guidance for ${formData.countries.map(code =>
                                            mockCountries.find(c => c.code === code)?.name
                                        ).filter(Boolean).join(', ')}`
                                        : 'Enter any FCDO travel restrictions or notes'
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Accommodation Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Accommodation Details
                        </Typography>
                        <IconButton
                            onClick={() => setExpandedSections(prev => ({ ...prev, accommodation: !prev.accommodation }))}
                        >
                            {expandedSections.accommodation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addAccommodation}
                            variant="outlined"
                            size="small"
                        >
                            Add Accommodation
                        </Button>
                    </Stack>

                    <Collapse in={expandedSections.accommodation}>
                        {formData.accommodations.length === 0 ? (
                            <Alert severity="info">
                                No accommodation entries yet. Click "Add Accommodation" to get started.
                            </Alert>
                        ) : (
                            <Stack spacing={2}>
                                {formData.accommodations.map((accommodation, index) => (
                                    <Card key={accommodation.id} variant="outlined">
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                                <Typography variant="subtitle1">
                                                    Accommodation {index + 1}
                                                </Typography>
                                                <Box sx={{ flexGrow: 1 }} />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeAccommodation(index)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        label="Check-in Date"
                                                        value={accommodation.checkInDate}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                accommodations: prev.accommodations.map(a =>
                                                                    a.id === accommodation.id
                                                                        ? { ...a, checkInDate: e.target.value }
                                                                        : a
                                                                )
                                                            }));
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        label="Check-out Date"
                                                        value={accommodation.checkOutDate}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                accommodations: prev.accommodations.map(a =>
                                                                    a.id === accommodation.id
                                                                        ? { ...a, checkOutDate: e.target.value }
                                                                        : a
                                                                )
                                                            }));
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Location"
                                                        value={accommodation.location}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                accommodations: prev.accommodations.map(a =>
                                                                    a.id === accommodation.id
                                                                        ? { ...a, location: e.target.value }
                                                                        : a
                                                                )
                                                            }));
                                                        }}
                                                        size="small"
                                                        placeholder="City or location"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Hotel Name"
                                                        size="small"
                                                        value={accommodation.hotelName}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                accommodations: prev.accommodations.map(a =>
                                                                    a.id === accommodation.id
                                                                        ? { ...a, hotelName: e.target.value }
                                                                        : a
                                                                )
                                                            }));
                                                        }}
                                                        placeholder="Enter hotel name"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Hotel Address"
                                                        value={accommodation.address}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                accommodations: prev.accommodations.map(a =>
                                                                    a.id === accommodation.id
                                                                        ? { ...a, address: e.target.value }
                                                                        : a
                                                                )
                                                            }));
                                                        }}
                                                        size="small"
                                                        placeholder="Hotel address"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Collapse>
                </Paper>

                {/* Travel Segments Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Travel & Transport Details
                        </Typography>
                        <IconButton
                            onClick={() => setExpandedSections(prev => ({ ...prev, travel: !prev.travel }))}
                        >
                            {expandedSections.travel ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addTravelSegment}
                            variant="outlined"
                            size="small"
                        >
                            Add Flight/Transport
                        </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1, fontWeight: 'bold' }}>
                        Please give details of each leg of the journey if not a direct flight.
                    </Typography>

                    <Collapse in={expandedSections.travel}>
                        {formData.travelSegments.length === 0 ? (
                            <Alert severity="info">
                                No travel segments yet. Click "Add Flight/Transport" to get started.
                            </Alert>
                        ) : (
                            <Stack spacing={2}>
                                {formData.travelSegments.map((segment, index) => (
                                    <Card key={segment.id} variant="outlined">
                                        <CardContent>
                                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                                <Typography variant="subtitle1">
                                                    Travel Segment {index + 1}
                                                </Typography>
                                                <Box sx={{ flexGrow: 1 }} />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            travelSegments: prev.travelSegments.filter(s => s.id !== segment.id)
                                                        }));
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        type="date"
                                                        label="Travel Date"
                                                        value={segment.travelDate}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, travelDate: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={3}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Travel Type</InputLabel>
                                                        <Select
                                                            value={segment.travelType}
                                                            label="Travel Type"
                                                            onChange={(e) => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    travelSegments: prev.travelSegments.map(s =>
                                                                        s.id === segment.id
                                                                            ? { ...s, travelType: e.target.value }
                                                                            : s
                                                                    )
                                                                }));
                                                            }}
                                                        >
                                                            <MenuItem value="Flight">Flight</MenuItem>
                                                            <MenuItem value="Train">Train</MenuItem>
                                                            <MenuItem value="Car">Car</MenuItem>
                                                            <MenuItem value="Other">Other</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        label="Flight/Transport Number"
                                                        value={segment.flightNumber}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, flightNumber: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        label="Booking Reference"
                                                        value={segment.bookingRef}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, bookingRef: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Route From"
                                                        value={segment.routeFrom}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, routeFrom: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Route To"
                                                        value={segment.routeTo}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, routeTo: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={2}>
                                                    <TextField
                                                        fullWidth
                                                        type="time"
                                                        label="Departure"
                                                        value={segment.departureTime}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, departureTime: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        size="small"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={2}>
                                                    <TextField
                                                        fullWidth
                                                        type="time"
                                                        label="Arrival"
                                                        value={segment.arrivalTime}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                travelSegments: prev.travelSegments.map(s =>
                                                                    s.id === segment.id
                                                                        ? { ...s, arrivalTime: e.target.value }
                                                                        : s
                                                                )
                                                            }));
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                        size="small"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Collapse>
                </Paper>

                {/* Pre-Deployment Checks Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Pre-Deployment Checks
                        </Typography>
                        <IconButton
                            onClick={() => setExpandedSections(prev => ({ ...prev, preDeployment: !prev.preDeployment }))}
                        >
                            {expandedSections.preDeployment ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>

                    <Collapse in={expandedSections.preDeployment}>
                        <Grid container spacing={3}>
                            {/* Bamboo Updated */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Is Bamboo up to date with your next of kin, emergency contact and medical details?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.bambooUpdated}
                                        onChange={(e) => setFormData(prev => ({ ...prev, bambooUpdated: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Pre-deployment Brief */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Pre-deployment Brief Completed (Duty officer and/or Security focal point)?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.preDeploymentBrief}
                                        onChange={(e) => setFormData(prev => ({ ...prev, preDeploymentBrief: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Happy to travel - location */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Please confirm you have been asked if you are happy to travel to the location(s)?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.happyToTravelLocation}
                                        onChange={(e) => setFormData(prev => ({ ...prev, happyToTravelLocation: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Happy to travel - companions */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Please confirm you have been asked if you are happy to travel with those travelling with you?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.happyToTravelCompanions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, happyToTravelCompanions: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* SAFE/HEAT Training */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">SAFE/HEAT Training Completed (if required)?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.safeHeatTraining}
                                        onChange={(e) => setFormData(prev => ({ ...prev, safeHeatTraining: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                        <FormControlLabel value="N/A" control={<Radio />} label="N/A" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Security Clearance */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Security Clearance (if required)"
                                    value={formData.securityClearance}
                                    onChange={(e) => setFormData(prev => ({ ...prev, securityClearance: e.target.value }))}
                                    helperText="Enter Y/N or clearance details"
                                />
                            </Grid>

                            {/* Medical Clearance */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Medical Clearance & Vaccinations?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.medicalClearance}
                                        onChange={(e) => setFormData(prev => ({ ...prev, medicalClearance: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Visa Obtained */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Visa obtained (if required)?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.visaObtained}
                                        onChange={(e) => setFormData(prev => ({ ...prev, visaObtained: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                        <FormControlLabel value="N/A" control={<Radio />} label="N/A" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Doctor Advised Not to Travel */}
                            <Grid item xs={12}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">
                                        Have you been advised by a doctor <strong>NOT</strong> to travel? (claims on our travel insurance may be refused if you have)
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.doctorAdvisedNotTravel}
                                        onChange={(e) => setFormData(prev => ({ ...prev, doctorAdvisedNotTravel: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Pre-existing Condition */}
                            <Grid item xs={12}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">If you have a pre-existing medical condition or physical impairment, claims on our travel insurance will only be paid for the difference between your condition before and after an accident. Please confirm you understand this</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.preExistingConditionUnderstood}
                                        onChange={(e) => setFormData(prev => ({ ...prev, preExistingConditionUnderstood: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>

                {/* Contact & Comms Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Contact & Communications
                        </Typography>
                        <IconButton
                            onClick={() => setExpandedSections(prev => ({ ...prev, contactComms: !prev.contactComms }))}
                        >
                            {expandedSections.contactComms ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>

                    <Collapse in={expandedSections.contactComms}>
                        <Grid container spacing={3}>
                            {/* WhatsApp Phone */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Phone for WhatsApp Group"
                                    value={formData.whatsappPhone}
                                    onChange={(e) => setFormData(prev => ({ ...prev, whatsappPhone: e.target.value }))}
                                    helperText="Include country code"
                                />
                            </Grid>

                            {/* Focal In-Country Contact Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Focal In-Country Contact Name"
                                    value={formData.focalInCountryName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, focalInCountryName: e.target.value }))}
                                />
                            </Grid>

                            {/* In-Country Contact Phone/Email */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="In-Country Contact Phone/Email"
                                    value={formData.focalInCountryContact}
                                    onChange={(e) => setFormData(prev => ({ ...prev, focalInCountryContact: e.target.value }))}
                                />
                            </Grid>

                            {/* Client Contact Name (Alternative) */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Client Contact Name (if different from above)"
                                    value={formData.clientContactNameAlt}
                                    onChange={(e) => setFormData(prev => ({ ...prev, clientContactNameAlt: e.target.value }))}
                                />
                            </Grid>

                            {/* Client Contact Details (Alternative) */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Client Contact Phone/Email"
                                    value={formData.clientContactDetailsAlt}
                                    onChange={(e) => setFormData(prev => ({ ...prev, clientContactDetailsAlt: e.target.value }))}
                                />
                            </Grid>

                            {/* Travelling with IOD */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Are you travelling with another IOD PARC employee, associate or partner?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.travellingWithIod}
                                        onChange={(e) => setFormData(prev => ({ ...prev, travellingWithIod: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Travelling With Name */}
                            {formData.travellingWithIod === 'Y' && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Colleague / Associate Names"
                                        value={formData.travellingWithName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, travellingWithName: e.target.value }))}
                                        placeholder="Enter names (one per line or separated by commas)"
                                        helperText="You can enter multiple names"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Collapse>
                </Paper>

                {/* Travel & Logistics Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Travel & Logistics
                        </Typography>
                        <IconButton
                            onClick={() => setExpandedSections(prev => ({ ...prev, travelLogistics: !prev.travelLogistics }))}
                        >
                            {expandedSections.travelLogistics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>

                    <Collapse in={expandedSections.travelLogistics}>
                        <Grid container spacing={3}>
                            {/* Airport Transfers */}
                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Airport Transfers arranged?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.airportTransfersArranged}
                                        onChange={(e) => setFormData(prev => ({ ...prev, airportTransfersArranged: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* In-country Transport */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="In-country Transport Arrangements"
                                    value={formData.inCountryTransport}
                                    onChange={(e) => setFormData(prev => ({ ...prev, inCountryTransport: e.target.value }))}
                                    helperText="Describe transport arrangements"
                                />
                            </Grid>

                            {/* Itinerary */}
                            <Grid item xs={12}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">If you have a planned itinerary, please confirm it has been forwarded to travel@iodparc.com</FormLabel>
                                    <RadioGroup
                                        row
                                        value={formData.itineraryForwarded}
                                        onChange={(e) => setFormData(prev => ({ ...prev, itineraryForwarded: e.target.value }))}
                                    >
                                        <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="N" control={<Radio />} label="No" />
                                        <FormControlLabel value="N/A" control={<Radio />} label="N/A" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>

                {/* Form Actions */}
                <Paper sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={saveAsDraft}
                        >
                            {isSaving ? 'Saving...' : 'Save Draft'}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
                            onClick={handleSubmit}
                            disabled={isLoading || progress < 70 || formData.status === 'submitted'}
                        >
                            {isLoading ? 'Submitting...' : formData.status === 'submitted' ? 'Form Submitted' : 'Submit Travel Form'}
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => {
                                if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
                                    setFormData(defaultFormData);
                                    setFormId(null);
                                    localStorage.removeItem('travel-form-draft');
                                    window.location.href = '/#/my-travel';
                                }
                            }}
                            disabled={isLoading || formData.status === 'submitted'}
                        >
                            Cancel
                        </Button>

                        <Box sx={{ flexGrow: 1 }} />

                        <Tooltip title="Preview email notification">
                            <Button
                                variant="text"
                                startIcon={<EmailIcon />}
                                onClick={() => {
                                    setEmailPreview(generateEmailPreview());
                                    setShowEmailDialog(true);
                                }}
                            >
                                Preview Email
                            </Button>
                        </Tooltip>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {formData.status === 'draft' && 'Form is automatically saved every 30 seconds'}
                            {formData.status === 'submitted' && 'Form has been submitted to travel@iodparc.com and is awaiting approval'}
                            {progress < 70 && formData.status === 'draft' && ' • Complete at least 70% to submit'}
                        </Typography>
                    </Box>
                </Paper>

                {/* Email Preview Dialog */}
                <Dialog
                    open={showEmailDialog}
                    onClose={() => setShowEmailDialog(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <EmailIcon />
                            Email Notification Preview
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            This email will be sent to travel@iodparc.com when the form is submitted:
                        </Typography>
                        <Paper sx={{
                            p: 3,
                            backgroundColor: '#1a1a1a',
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            color: '#ffffff',
                            border: '1px solid',
                            borderColor: 'grey.400',
                            minHeight: '200px',
                            fontSize: '0.875rem',
                            lineHeight: 1.6
                        }}>
                            {emailPreview}
                        </Paper>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowEmailDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert onClose={() => setSnackbarOpen(false)} severity="success">
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

export default TravelFormPage;

